'use client';

import { useEffect, useState } from 'react';
import { fetchWithAuth } from '@/lib/utils/api-auth';

/**
 * The dashboard's normalized billing snapshot. `plan` is clamped to the four
 * known tiers; every numeric field falls back to a safe default when the API
 * payload is missing or malformed. Imported back into `DashboardContext` for the
 * context value type; the type was module-private before this extraction, so the
 * public surface is unchanged.
 */
export type BillingSummary = {
  plan: 'FREE' | 'PRO' | 'BUSINESS' | 'ENTERPRISE';
  creditBalance: number;
  promptsUsed: number;
  promptLimitPerMonth: number | null;
  dailyFreeCreditsPerDay: number;
  dailyFreeCreditsRemainingToday: number;
};

interface UseDashboardBillingParams {
  /** `isLoading` from `useAuth()` — the effect no-ops until auth resolves. */
  authLoading: boolean;
  /** `isAuthenticated` from `useAuth()` — anonymous sessions reset billing. */
  isAuthenticated: boolean;
  /** `user?.id` — a re-trigger dep so the snapshot reloads on identity change. */
  userId: string | undefined;
  /** `activeWorkspace?.id` — a re-trigger dep so the snapshot reloads on workspace change. */
  activeWorkspaceId: string | undefined;
}

interface UseDashboardBillingResult {
  billingSummary: BillingSummary | null;
  billingLoading: boolean;
  isFree: boolean;
}

/**
 * `useDashboardBilling` — owns the `/api/billing/me` snapshot the dashboard
 * exposes for plan-gating and usage display.
 *
 * Extracted verbatim from `DashboardContext` (Phase 1.6 (4)). The effect body —
 * the cancellation flag, the two microtask-deferred setState points
 * (`setBillingLoading(true)` and the anonymous-reset `setBillingSummary(null)`),
 * the `response.ok` / snapshot-shape guards, the per-field clamp table, and the
 * `catch` / `finally` cleanup — is byte-for-byte identical to the original. The
 * only change is that the closed-over `authLoading` / `isAuthenticated` and the
 * `user?.id` / `activeWorkspace?.id` re-trigger deps are injected as params; the
 * values flow back into the provider's context `value` under the same field
 * names (`billingSummary`, `billingLoading`, `isFree`).
 */
export function useDashboardBilling({
  authLoading,
  isAuthenticated,
  userId,
  activeWorkspaceId,
}: UseDashboardBillingParams): UseDashboardBillingResult {
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      // Microtask-defer the reset so the setState happens in an async
      // callback rather than synchronously inside the effect body.
      Promise.resolve().then(() => {
        setBillingSummary(null);
      });
      return;
    }

    let cancelled = false;
    // Microtask-defer the loading flag so the synchronous setState during
    // the effect body becomes async-callback setState. The user-perceptible
    // delay is sub-frame; the fetch hasn't resolved yet either way.
    Promise.resolve().then(() => {
      if (!cancelled) setBillingLoading(true);
    });

    void fetchWithAuth('/api/billing/me', { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) return null;
        return response.json().catch(() => null);
      })
      .then((payload) => {
        if (cancelled) return;
        const snapshot = payload?.data?.snapshot;
        if (!snapshot || typeof snapshot !== 'object') {
          setBillingSummary(null);
          return;
        }
        setBillingSummary({
          plan:
            snapshot.plan === 'PRO' || snapshot.plan === 'BUSINESS' || snapshot.plan === 'ENTERPRISE'
              ? snapshot.plan
              : 'FREE',
          creditBalance: typeof snapshot.creditBalance === 'number' ? snapshot.creditBalance : 0,
          promptsUsed: typeof snapshot.promptsUsed === 'number' ? snapshot.promptsUsed : 0,
          promptLimitPerMonth: typeof snapshot.promptLimitPerMonth === 'number' ? snapshot.promptLimitPerMonth : null,
          dailyFreeCreditsPerDay:
            typeof snapshot.dailyFreeCreditsPerDay === 'number' ? snapshot.dailyFreeCreditsPerDay : 3,
          dailyFreeCreditsRemainingToday:
            typeof snapshot.dailyFreeCreditsRemainingToday === 'number' ? snapshot.dailyFreeCreditsRemainingToday : 0,
        });
      })
      .catch(() => {
        if (!cancelled) setBillingSummary(null);
      })
      .finally(() => {
        if (!cancelled) setBillingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, userId, activeWorkspaceId]);

  const isFree = !billingSummary || billingSummary.plan === 'FREE';

  return {
    billingSummary,
    billingLoading,
    isFree,
  };
}
